import type {
  Client,
  ClientCreated,
  ClientDetail,
  Connection,
  ConnectionHealth,
  FeatureConfig,
  LookerCredentials,
  PendingBatch,
  Platform,
  Report,
  ReportLink,
  SyncRun,
  WindsorAccount,
} from './types';

/**
 * Typed API client.
 *
 * `credentials: 'same-origin'` rather than 'include': the SPA is served by
 * FastAPI from the same origin in production, and proxied through Vite in
 * development, so the session cookie travels without CORS being involved.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let body: unknown = text;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* non-JSON error page */
  }

  if (!response.ok) {
    const detail =
      typeof body === 'object' && body !== null && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `Request failed (${response.status})`;
    throw new ApiError(detail, response.status);
  }

  return body as T;
}

export const api = {
  // --- Auth ---------------------------------------------------------------
  login: (password: string) =>
    request<{ authenticated: boolean }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),

  session: () => request<{ authenticated: boolean; role: string | null }>('/api/auth/session'),

  config: () => request<FeatureConfig>('/api/config'),

  // --- Clients ------------------------------------------------------------
  listClients: () => request<Client[]>('/api/clients'),

  health: () => request<ConnectionHealth[]>('/api/clients/health'),

  createClient: (payload: { slug: string; name: string; timezone: string }) =>
    request<ClientCreated>('/api/clients', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getClient: (slug: string) => request<ClientDetail>(`/api/clients/${slug}`),

  rotateCredentials: (slug: string) =>
    request<LookerCredentials>(`/api/clients/${slug}/rotate`, { method: 'POST' }),

  deleteClient: (slug: string) =>
    request<void>(`/api/clients/${slug}`, { method: 'DELETE' }),

  /** purge=true also deletes every report row the account produced. */
  deleteConnection: (connectionId: number, purge = false) =>
    request<{ rows_deleted: number }>(
      `/api/clients/connections/${connectionId}${purge ? '?purge=true' : ''}`,
      { method: 'DELETE' },
    ),

  // --- Windsor ------------------------------------------------------------
  windsorAccounts: (platform: Platform) =>
    request<WindsorAccount[]>(`/api/windsor/accounts?platform=${platform}`),

  linkWindsor: (slug: string, accountId: string, platform: Platform) =>
    request<Connection>(`/api/clients/${slug}/windsor`, {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId, platform }),
    }),

  // --- OAuth account picker -----------------------------------------------
  getPending: (id: string) => request<PendingBatch>(`/api/pending/${id}`),

  confirmPending: (id: string, externalIds: string[]) =>
    request<Connection[]>(`/api/pending/${id}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ external_ids: externalIds }),
    }),

  discardPending: (id: string) => request<void>(`/api/pending/${id}`, { method: 'DELETE' }),

  // --- Sync ---------------------------------------------------------------
  triggerSync: (slug?: string, days?: number) =>
    request<{ accepted: boolean; client: string }>('/api/sync', {
      method: 'POST',
      body: JSON.stringify({ slug, days }),
    }),

  runs: (connectionId: number) => request<SyncRun[]>(`/api/sync/runs/${connectionId}`),

  // --- Reports --------------------------------------------------------------
  /** Public: the token is the credential, so this works without a session. */
  report: (token: string, period: URLSearchParams) =>
    request<Report>(`/api/reports/${token}?${period.toString()}`),

  reportLinks: (slug: string) => request<ReportLink[]>(`/api/clients/${slug}/report-links`),

  rotateReportLink: (connectionId: number) =>
    request<ReportLink>(`/api/connections/${connectionId}/rotate-report-link`, {
      method: 'POST',
    }),

  setBranding: (slug: string, payload: { logo_url: string | null; accent_color: string | null }) =>
    request<Client>(`/api/clients/${slug}/branding`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
};

/** OAuth flows are full-page redirects, not fetches. */
export const oauthStartUrl = (platform: 'meta' | 'linkedin', clientId: number) =>
  `/api/oauth/${platform}/start?client=${clientId}`;
